import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, session } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  // Load the account level this user is allowed to manage.
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!user || user.system_role === 'coach') {
        setCoaches([]);
        return;
      }
      try {
        const { data } = await api('/users');
        setCoaches(data || []);
      } catch (err) {
        console.error('Error in fetchCoaches:', err);
        setCoaches([]);
      }
    };

    fetchCoaches();
  }, [user]);

  // Check for saved user session on initial load
  useEffect(() => {
    const restoreSession = async () => {
      if (!session.get()) {
        localStorage.removeItem('marketing_mentor_user');
        setLoading(false);
        return;
      }
      try {
        const { user: verifiedUser } = await api('/auth/me');
        setUser(verifiedUser);
        localStorage.setItem('marketing_mentor_user', JSON.stringify(verifiedUser));
      } catch {
        session.clear();
        localStorage.removeItem('marketing_mentor_user');
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  // Passwordless email-code login.
  const requestLoginCode = async (email, turnstileToken) => {
    try { await api('/auth/request-code', { method: 'POST', body: JSON.stringify({ email, turnstileToken }) }); return true; }
    catch (err) { console.error('Error requesting login code:', err); return false; }
  };

  const login = async (email, code) => {
    try {
      const { user: userData, token } = await api('/auth/verify-code', { method: 'POST', body: JSON.stringify({ email, code }) });
      session.set(token); setUser(userData); localStorage.setItem('marketing_mentor_user', JSON.stringify(userData)); return true;
    } catch (err) {
      console.error('Error in login:', err); return false;
    }
  };

  const logout = () => {
    api('/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    localStorage.removeItem('marketing_mentor_user');
    session.clear();
  };

  // Create a new coach
  const createCoach = async (coachData) => {
    try {
      const { password: _ignoredPassword, ...allocation } = coachData;
      const { data } = await api('/users', { method: 'POST', body: JSON.stringify(allocation) });
      setCoaches(prev => [...prev, data]);
      return data;
    } catch (err) {
      console.error('Error in createCoach:', err);
      throw err;
    }
  };

  // Update an existing coach
  const updateCoach = async (coachData) => {
    try {
      const { id, ...updateData } = coachData;
      
      // If password is empty, remove it from the update data
      if (updateData.password === '') {
        delete updateData.password;
      }
      
      const { data } = await api(`/users/${encodeURIComponent(id)}`, {
        method: 'PATCH', body: JSON.stringify(updateData)
      });

      // Update coaches list
      setCoaches(prev => 
        prev.map(coach => coach.id === id ? { ...coach, ...updateData } : coach)
      );
      
      // Update current user if it's the same coach
      if (user && user.id === id) {
        const updatedUser = { ...user, ...updateData };
        setUser(updatedUser);
        localStorage.setItem('marketing_mentor_user', JSON.stringify(updatedUser));
      }
      
      return data;
    } catch (err) {
      console.error('Error in updateCoach:', err);
      throw err;
    }
  };

  // Archive a coach (instead of deleting)
  const archiveCoach = async (coachId) => {
    try {
      await api(`/users/${encodeURIComponent(coachId)}`, {
        method: 'PATCH', body: JSON.stringify({ is_archived: true })
      });

      // Update coaches list
      setCoaches(prev => 
        prev.map(coach => 
          coach.id === coachId ? { ...coach, is_archived: true } : coach
        )
      );
      
      // If the archived coach is the current user, log them out
      if (user && user.id === coachId) {
        logout();
      }
      
      return true;
    } catch (err) {
      console.error('Error in archiveCoach:', err);
      throw err;
    }
  };

  // Restore an archived coach
  const restoreCoach = async (coachId) => {
    try {
      await api(`/users/${encodeURIComponent(coachId)}`, {
        method: 'PATCH', body: JSON.stringify({ is_archived: false })
      });

      // Update coaches list
      setCoaches(prev => 
        prev.map(coach => 
          coach.id === coachId ? { ...coach, is_archived: false } : coach
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error in restoreCoach:', err);
      throw err;
    }
  };

  // Toggle archived coaches visibility
  const toggleShowArchived = () => {
    setShowArchived(prev => !prev);
  };

  // Get active and/or archived coaches based on current filter
  const getFilteredCoaches = () => {
    return showArchived 
      ? coaches 
      : coaches.filter(coach => !coach.is_archived);
  };

  const value = {
    user,
    requestLoginCode,
    login,
    logout,
    loading,
    coaches: getFilteredCoaches(),
    allCoaches: coaches,
    showArchived,
    toggleShowArchived,
    createCoach,
    updateCoach,
    archiveCoach, // Changed from deleteCoach to archiveCoach
    restoreCoach
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
